import { useEffect, useState } from 'react';
import { itemWord } from '../plural';
import {
  formatFacetSelection,
  loadFacets,
  parseFacetSelection,
  toggleFacetValue,
  type Facet,
} from './facets';

/**
 * Parametricke filtry nad mrizkou zbozi.
 *
 * NEJDULEZITEJSI VEC NA TEHLE KOMPONENTE JE, KDY SE NEVYKRESLI.
 *
 * Informacni parametry ma jen ~18 % zverejneneho katalogu (1 809 z 10 211
 * polozek), a rozlozene jsou nerovnomerne: `Příchuť` u 1 606 polozek, ale
 * `Barva`, `Délka` i `Sisal` u tri. Filtry proto NEJSOU celoshopova funkce.
 * Server posila jen parametry, ktere v danem vysledku maji aspon dve ruzne
 * hodnoty (jinak neni z ceho vybirat), a kdyz nezbyde zadny, tenhle blok
 * zmizi cely — zadny nadpis, zadna prazdna nabidka. JEDINA VYJIMKA je filtr
 * v adrese: dokud neco filtruje, musi zustat cim to vypnout (viz niz).
 *
 * A KDYZ SE VYKRESLI, RIKA PRAVDU O POKRYTI. U parametru, ktery se tyka jen
 * casti vysledku, stoji „u 12 z 1 466 položek". Bez toho vypada filtr, ze
 * pracuje s celou kategorii, a zakaznik si z prazdneho vysledku odvodi, ze
 * takove zbozi nemame — pritom jen nemame udaj.
 */
export function ParameterFacets({
  search,
  category,
  brand,
  inStock,
  value,
  total,
  onChange,
}: {
  readonly search: string;
  readonly category: string;
  readonly brand: string;
  readonly inStock: boolean;
  /** Obsah `?parametry=` z adresy. */
  readonly value: string;
  /** Kolik polozek ma vysledek — jmenovatel pro pokryti. */
  readonly total: number;
  readonly onChange: (next: string) => void;
}) {
  const [facets, setFacets] = useState<readonly Facet[]>([]);

  /**
   * HLEDANI SE ODKLADA, ZBYTEK NE.
   *
   * `?hledat=` se meni po KAZDEM znaku, ale dotaz na facety je z cele stranky
   * ten nejdrazsi — pocita pocty pro vsechny hodnoty vsech parametru.
   * Bez odkladu stalo napsani slova „Granule" sedm dotazu na facety misto
   * jednoho; mrizka zbozi pritom odklad (250 ms) uz mela, takze se posilal
   * jen ten drazsi z obou.
   *
   * Odklada se VYHRADNE hledani: klik na chip, zmena kategorie, znacky nebo
   * „jen skladem" je jedno gesto a ma se projevit hned.
   *
   * Prazdne hledani (smazani policka) se nedebouncuje — zakaznik ceka, ze se
   * vyloha vrati okamzite.
   */
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    if (search === '') {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let alive = true;
    void loadFacets({ search: debouncedSearch, category, brand, inStock, parametry: value }).then(
      (result) => {
        if (alive) setFacets(result);
      },
    );
    return () => {
      alive = false;
    };
  }, [debouncedSearch, category, brand, inStock, value]);

  const selection = parseFacetSelection(value);
  const selectedCount = [...selection.values()].reduce((sum, values) => sum + values.length, 0);

  /**
   * ZADNY FACET A ZADNY FILTR = blok zmizi cely. Zadny nadpis, zadna prazdna
   * nabidka.
   *
   * ZADNY FACET, ALE FILTR V ADRESE = zustava aspon tlacitko „Zrušit filtry".
   * Preklep nebo rucne upravena adresa (`?parametry=neznamy:x`) da prazdny
   * vypis i prazdne facety, a kdyz zmizel i tenhle blok, nebylo na strance
   * NIC, cim filtr vypnout — zakaznik se ven dostal jen prepsanim URL.
   */
  if (facets.length === 0 && selectedCount === 0) return null;

  return (
    <div className="filters">
      {selectedCount > 0 && (
        <div className="chips">
          {/*
            Vypnout vsechno na jedno kliknuti. Bez toho se zakaznik z peti
            zaskrtnutych hodnot proklikava zpatky po jedne.
          */}
          <button type="button" className="chip" onClick={() => onChange('')}>
            Zrušit filtry ({selectedCount})
          </button>
        </div>
      )}

      {facets.map((facet) => (
        <div key={facet.slug}>
          <p className="muted small">
            {facet.name}
            {/*
              Pokryti se pise jen tam, kde parametr NEPOKRYVA cely vysledek.
              U parametru s vlastnim vyberem se pocita bez nej, takze by
              jmenovatel neodpovidal vypisu — pak se radeji nepise nic.
            */}
            {facet.values.every((item) => !item.selected) && facet.covered < total && (
              <> — u {facet.covered} z {total} {itemWord(total)}</>
            )}
          </p>
          <div className="chips">
            {facet.values.map((item) => (
              <button
                key={item.slug}
                type="button"
                className={item.selected ? 'chip active' : 'chip'}
                aria-pressed={item.selected}
                // Hodnota, ktera by dala prazdny vypis, jde jen ODSKRTNOUT.
                disabled={item.count === 0 && !item.selected}
                onClick={() =>
                  onChange(formatFacetSelection(toggleFacetValue(selection, facet.slug, item.slug)))
                }
              >
                {item.label}
                <span className="chip-count">{item.count}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
